import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiPost, useApiPut, useApiDelete, useApiMutation, useApiBlobMutation } from '../../hooks/use-api';
import {
  PlatformEquipmentListResponse,
  PlatformEquipmentDetailResponse,
  CreatePlatformEquipmentRequest,
  UpdatePlatformEquipmentRequest
} from './types';

// Query Keys
export const platformEquipmentKeys = {
  all: ['platform-equipment'],
  lists: () => [...platformEquipmentKeys.all, 'list'],
  list: (filters: Record<string, any>) => [...platformEquipmentKeys.lists(), JSON.stringify(filters)],
  details: () => [...platformEquipmentKeys.all, 'detail'],
  detail: (id: number) => [...platformEquipmentKeys.details(), id.toString()],
  categories: () => [...platformEquipmentKeys.all, 'categories'],
};

// Hooks for fetching data
export function usePlatformEquipmentList(
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    category?: number;
    includeInactive?: boolean;
  } = {}
) {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.category) queryParams.append('category', params.category.toString());
  if (params.includeInactive) queryParams.append('includeInactive', params.includeInactive.toString());

  const endpoint = `/platform/equipment${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return useApiQuery<PlatformEquipmentListResponse>(
    platformEquipmentKeys.list(params),
    endpoint
  );
}

export function usePlatformEquipmentDetail(id: number) {
  return useApiQuery<PlatformEquipmentDetailResponse>(
    platformEquipmentKeys.detail(id),
    `/platform/equipment/${id}`,
    {
      enabled: !!id,
    }
  );
}

export function usePlatformEquipmentCategories() {
  return useApiQuery<Array<{ value: number; name: string }>>(
    platformEquipmentKeys.categories(),
    '/platform/equipment/categories'
  );
}

// Hooks for mutations
export function useCreatePlatformEquipment() {
  const queryClient = useQueryClient();

  return useApiPost<{ id: number; message: string }, any, CreatePlatformEquipmentRequest>(
    '/platform/equipment',
    {
      onSuccess: () => {
        // Invalidate and refetch equipment list
        queryClient.invalidateQueries({ queryKey: platformEquipmentKeys.lists() });
      },
    }
  );
}

export function useUpdatePlatformEquipment() {
  const queryClient = useQueryClient();

  return useApiPut<{ message: string }, any, UpdatePlatformEquipmentRequest & { id: number }>(
    (variables) => `/platform/equipment/${variables.id}`,
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch equipment list and specific item
        queryClient.invalidateQueries({ queryKey: platformEquipmentKeys.lists() });
        queryClient.invalidateQueries({ queryKey: platformEquipmentKeys.detail(variables.id) });
      },
    }
  );
}

export function useDeletePlatformEquipment() {
  const queryClient = useQueryClient();

  return useApiDelete<{ message: string }, any, { id: number }>(
    (variables) => `/platform/equipment/${variables.id}`,
    {
      onSuccess: () => {
        // Invalidate and refetch equipment list
        queryClient.invalidateQueries({ queryKey: platformEquipmentKeys.lists() });
      },
    }
  );
}

export function useReactivatePlatformEquipment() {
  const queryClient = useQueryClient();

  return useApiMutation<{ message: string }, any, { id: number }>(
    'POST',
    (variables) => `/platform/equipment/${variables.id}/reactivate`,
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch equipment list and specific item
        queryClient.invalidateQueries({ queryKey: platformEquipmentKeys.lists() });
        queryClient.invalidateQueries({ queryKey: platformEquipmentKeys.detail(variables.id) });
      },
    }
  );
}

export function useReorderPlatformEquipment() {
  const queryClient = useQueryClient();

  return useApiMutation<{ message: string }, any, { equipmentIds: number[] }>(
    'POST',
    () => '/platform/equipment/reorder',
    {
      onSuccess: () => {
        // Invalidate and refetch equipment list to reflect new order
        queryClient.invalidateQueries({ queryKey: platformEquipmentKeys.lists() });
      },
    }
  );
}

// CSV Export/Import hooks
export function useExportPlatformEquipmentCsv() {
  return useApiBlobMutation<any, {
    includeInactive?: boolean;
    search?: string;
    category?: number;
  }>(
    'GET',
    (variables) => {
      const queryParams = new URLSearchParams();
      if (variables.includeInactive) queryParams.append('includeInactive', 'true');
      if (variables.search) queryParams.append('search', variables.search);
      if (variables.category) queryParams.append('category', variables.category.toString());
      
      return `/platform/equipment/export/csv${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    }
  );
}

export function useImportPlatformEquipmentCsv() {
  const queryClient = useQueryClient();

  return useApiMutation<
    { message: string; summary: { created: number; updated: number; skipped: number }; errors: string[] },
    any,
    { file: File }
  >(
    'POST',
    () => '/platform/equipment/import/csv',
    {
      onSuccess: () => {
        // Invalidate and refetch equipment list after import
        queryClient.invalidateQueries({ queryKey: platformEquipmentKeys.lists() });
      },
      requestType: 'multipart',
    }
  );
}